import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { load } from "@cashfreepayments/cashfree-js";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { api, formatINR } from "../lib/api";
import { toast } from "sonner";
import { Lock, MapPin, ShieldCheck } from "lucide-react";

export default function Checkout() {
  const { cart, cartSubtotal, clearCart } = useShop();
  const { user } = useAuth();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", address: "",
    city: "", state: "", pincode: "",
  });
  const [loading, setLoading] = useState(false);
  const abandonTimer = useRef(null);
  const lastSaved = useRef("");

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || user.name || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
    }));
    api.get("/account/addresses").then((r) => setSavedAddresses(r.data)).catch(() => {});
  }, [user]);

  // Debounced save of abandoned cart snapshot whenever email is provided
  useEffect(() => {
    if (abandonTimer.current) clearTimeout(abandonTimer.current);
    const emailOk = /^\S+@\S+\.\S+$/.test(form.email);
    if (!emailOk || cart.length === 0) return;
    const key = `${form.email}|${cart.map((i) => `${i.product_id}x${i.quantity}`).join(",")}|${cartSubtotal}`;
    if (key === lastSaved.current) return;
    abandonTimer.current = setTimeout(() => {
      api.post("/cart/abandoned", {
        email: form.email,
        name: form.customer_name,
        items: cart.map((i) => ({
          product_id: i.product_id, name: i.name, image: i.image,
          price: i.price, quantity: i.quantity,
        })),
        subtotal: cartSubtotal,
      }).then(() => { lastSaved.current = key; }).catch(() => {});
    }, 1200);
    return () => abandonTimer.current && clearTimeout(abandonTimer.current);
  }, [form.email, form.customer_name, cart, cartSubtotal]);

  const applyAddress = (a) => {
    setForm((f) => ({
      ...f,
      phone: a.phone || f.phone,
      address: a.line1,
      city: a.city,
      state: a.state,
      pincode: a.pincode,
    }));
    toast.success(`Using ${a.label} address`);
  };

  const shipping = cartSubtotal > 999 ? 0 : 99;
  const total = cartSubtotal + shipping;

  const submit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;
    setLoading(true);
    try {
      // Step 1: create the local MileForge order
      const r = await api.post("/orders", {
        ...form,
        payment_method: "cashfree",
        items: cart.map((i) => ({
          product_id: i.product_id, name: i.name, image: i.image,
          price: i.price, quantity: i.quantity,
        })),
        subtotal: cartSubtotal, shipping, total,
      });
      const orderNumber = r.data.order_number;

      // Step 2: create Cashfree session & launch Hosted Checkout
      const cf = await api.post("/payments/cashfree/create", { order_number: orderNumber });
      const { payment_session_id, mode } = cf.data;

      const cashfree = await load({ mode: mode === "production" ? "production" : "sandbox" });
      if (!cashfree) throw new Error("Cashfree SDK failed to load");

      clearCart();
      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_self",
      });
      // Cashfree will redirect the browser; nothing else to do here.
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Order failed. Try again.";
      toast.error(String(detail));
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6">
        <h2 className="font-display text-3xl uppercase">Your bag is empty</h2>
        <Link to="/shop" className="btn-orange mt-6">Start Shopping</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="overline mb-3">Almost There</div>
      <h1 className="section-title text-4xl sm:text-5xl mb-10">Checkout</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-7 space-y-7">
          {user && savedAddresses.length > 0 && (
            <div data-testid="saved-addresses-block">
              <h3 className="overline mb-3">Use a Saved Address</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {savedAddresses.map((a) => (
                  <button
                    type="button"
                    key={a.id}
                    data-testid={`use-address-${a.id}`}
                    onClick={() => applyAddress(a)}
                    className="text-left border border-white/10 p-4 hover:border-[#D96A1B]/60 transition-colors"
                  >
                    <div className="flex items-center gap-2 overline mb-1"><MapPin size={12} /> {a.label}</div>
                    <div className="text-xs text-white/70 font-body line-clamp-2">{a.line1}, {a.city}, {a.state} – {a.pincode}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {!user && (
            <div className="border border-dashed border-white/15 p-4 text-sm font-body text-white/70" data-testid="guest-checkout-note">
              Checking out as a guest. <Link to="/signin" className="text-[#D96A1B] hover:underline">Sign in</Link> to use saved addresses and track this order from your account.
            </div>
          )}
          <div>
            <h3 className="overline mb-4">Contact</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="co-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Full Name" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
              <input data-testid="co-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (10 digits)" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
            </div>
            <input data-testid="co-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-[#111] border border-white/15 px-4 py-3 mt-4 focus:outline-none focus:border-[#D96A1B]" />
          </div>

          <div>
            <h3 className="overline mb-4">Shipping</h3>
            <input data-testid="co-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street Address" className="w-full bg-[#111] border border-white/15 px-4 py-3 mb-4 focus:outline-none focus:border-[#D96A1B]" />
            <div className="grid sm:grid-cols-3 gap-4">
              <input data-testid="co-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
              <input data-testid="co-state" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
              <input data-testid="co-pincode" required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="Pincode" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B]" />
            </div>
          </div>

          <div>
            <h3 className="overline mb-4">Payment</h3>
            <div className="border border-white/10 p-4 flex items-center gap-3">
              <ShieldCheck size={20} className="text-[#D96A1B] shrink-0" />
              <span className="text-sm font-body text-white/80">
                You'll be redirected to Cashfree's secure checkout to pay via UPI, Card or Netbanking.
              </span>
            </div>
            <p className="text-xs text-white/50 mt-3 font-body flex items-center gap-2">
              <ShieldCheck size={12} className="text-[#D96A1B]" />
              Payments securely processed by Cashfree. Your card & UPI details never touch our servers.
            </p>
          </div>
        </div>

        {/* Summary */}
        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start border border-white/10 p-6 space-y-5">
          <h3 className="overline">Order Summary</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {cart.map((i) => (
              <div key={i.product_id} className="flex gap-3 items-start">
                <img src={i.image} alt="" className="w-14 h-16 object-cover" />
                <div className="flex-1">
                  <div className="font-display uppercase text-xs tracking-tight line-clamp-2">{i.name}</div>
                  <div className="text-xs text-white/50 font-body">Qty {i.quantity}</div>
                </div>
                <div className="font-display text-sm">{formatINR(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm font-body">
            <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span>{formatINR(cartSubtotal)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Shipping</span><span>{shipping === 0 ? "FREE" : formatINR(shipping)}</span></div>
            <div className="flex justify-between font-display text-2xl pt-2 border-t border-white/10 mt-2">
              <span>Total</span><span data-testid="co-total" className="text-[#D96A1B]">{formatINR(total)}</span>
            </div>
          </div>
          <button data-testid="co-place-order" disabled={loading} className="btn-orange w-full justify-center">
            {loading ? "Opening secure checkout…" : `Pay Securely — ${formatINR(total)}`}
          </button>
          <p className="text-[10px] text-white/40 text-center flex items-center justify-center gap-1.5">
            <Lock size={10} /> 256-bit SSL · Cashfree Verified Merchant
          </p>
        </aside>
      </form>
    </div>
  );
}
